import Input from "../components/input/Input";
import * as Yup from "yup";
import Button from "../components/button/Button";
import { Formik, Form } from "formik";
import Link from "../components/Link/Link";
import Checkbox from "../components/checkbox/Checkbox";
import Paragraph from "../components/paragraph/Paragraph";
import { ReactComponent as LoginIlustration } from "../assets/images/login-ilustration.svg";
import { ReactComponent as RegisterIlustration } from "../assets/images/register-ilustration.svg";
import { useMediaQuery } from "react-responsive";
import { useLogin } from "../hooks/useLogin";
import { useRegister } from "../hooks/useRegister";
import { AuthTab, ROUTES } from "../constants/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthPersistStore from "../store/authPersistStore";
import useSubscriptionStore from "../store/subscriptionStore";
import { useMutateSubscriptionDetails } from "../hooks/useMutateSubscriptionDetails";
import { REFERRAL_PARAM, REFERRAL_SEMINAR } from "../constants/url-params";
import { trim } from "../utils/trim";
import { isOneTimePlanType, isRecurringPlanType } from "../constants/plans";

const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email("Email adresa nije ispravna")
    .required("Obavezno polje"),
  password: Yup.string()
    .required("Obavezno polje")
    .min(8, "Mora imati najmanje 8 karaktera"),
});

const registerValidationSchema = Yup.object({
  email: Yup.string()
    .email("Email adresa nije ispravna")
    .required("Obavezno polje"),
  password: Yup.string()
    .required("Obavezno polje")
    .min(8, "Mora imati najmanje 8 karaktera")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Najmanje 8 karaktera (veliko i malo slovo, broj)"
    ),
  confirmPassword: Yup.string()
    .required("Obavezno polje")
    .oneOf([Yup.ref("password")], "Lozinke se ne poklapaju"),
  policy: Yup.bool()
    .oneOf([true], "Obavezno polje")
    .required("Obavezno polje"),
});

const Login = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: AuthTab =
    searchParams.get("tab") === "register" ? "register" : "login";

  const referralValue = searchParams.get(REFERRAL_PARAM);
  if (referralValue === REFERRAL_SEMINAR) {
    // TODO: na koraku za odabir plana prikazati samo 2 meseca
  }

  const isDesktop = useMediaQuery({ query: "(min-width: 1200px)" });
  const navigate = useNavigate();
  const { mutate: loginMutate, isLoading: isLoginLoading } = useLogin();
  const { mutate: registerMutate, isLoading: isRegisterLoading } =
    useRegister();
  const { setLogin } = useAuthPersistStore();
  const { setSubscriptionType, subscriptionType } = useSubscriptionStore();
  const {
    mutate: getSubscriptionDetails,
    isLoading: isGetSubscriptionDetailsLoading,
  } = useMutateSubscriptionDetails();

  const setActiveTab = (tab: AuthTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    setSearchParams(nextParams, { replace: true });
  };

  const Illustration =
    activeTab === "register" ? RegisterIlustration : LoginIlustration;

  return (
    <div className="auth">
      <div className="auth_layout">
        <div className="auth_content">
          <header key={`header-${activeTab}`} className="auth_header">
            {activeTab === "login" ? (
              <>
                <h1>Dobrodošli nazad</h1>
                <Paragraph variant="p2">
                  Prijavi se i nastavi put ka ličnom unapređenju.
                </Paragraph>
              </>
            ) : (
              <>
                <h1>Kreiraj nalog</h1>
                <Paragraph variant="p2">
                  Tvoj put ka boljem životu kreće ovde.
                </Paragraph>
              </>
            )}
          </header>

          <div
            className="auth_tabs"
            role="tablist"
            aria-label="Prijava ili registracija"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "login"}
              className={trim(
                `auth_tab ${activeTab === "login" ? "auth_tab--active" : ""}`
              )}
              onClick={() => setActiveTab("login")}
            >
              Prijavi se
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "register"}
              className={trim(
                `auth_tab ${
                  activeTab === "register" ? "auth_tab--active" : ""
                }`
              )}
              onClick={() => setActiveTab("register")}
            >
              Registruj se
            </button>
          </div>

          <div key={activeTab} className="auth_panel" role="tabpanel">
            {activeTab === "login" ? (
              <>
                <Formik
                  onSubmit={(payload, { setFieldError }) => {
                    loginMutate(
                      {
                        email: payload.email,
                        password: payload.password,
                        role: "User",
                      },
                      {
                        onSuccess: (data) => {
                          // @ts-ignore
                          const { accessToken, user } = data;
                          setLogin(accessToken, user);

                          if (!user?.userProfile) {
                            navigate(ROUTES.createProfile);
                            return;
                          }

                          if (subscriptionType) {
                            getSubscriptionDetails(
                              {},
                              {
                                onSuccess: (variables) => {
                                  if (
                                    // @ts-ignore
                                    variables?.subscriptionType === "Monthly" ||
                                    // @ts-ignore
                                    variables?.subscriptionType === "Yearly"
                                  ) {
                                    navigate(ROUTES.home);
                                  } else {
                                    navigate(ROUTES.confirmPlan, {
                                      state: { subscriptionType },
                                    });
                                    setSubscriptionType({
                                      subscriptionType: null,
                                    });
                                  }
                                },
                              }
                            );
                            return;
                          }

                          navigate(ROUTES.home);
                        },
                        onError: () => {
                          setFieldError(
                            "email",
                            "Pogrešan email ili lozinka. Proverite unesene podatke."
                          );
                        },
                      }
                    );
                  }}
                  validationSchema={loginValidationSchema}
                  validateOnChange={false}
                  initialValues={{ email: "", password: "" }}
                >
                  <Form className="auth_form">
                    <Input
                      name="email"
                      label="Email"
                      placeholder="npr. ime@email.com"
                      type="email"
                      autoFocus
                      autoComplete="email"
                    />
                    <Input
                      name="password"
                      label="Lozinka"
                      type="password"
                      placeholder="Unesite lozinku"
                      autoComplete="current-password"
                    />

                    <div className="auth_form-meta">
                      <Link
                        variant="secondary"
                        label="Zaboravili ste lozinku?"
                        onClick={() => navigate(ROUTES.forgotPassword)}
                      />
                    </div>

                    <Button
                      variant="primary"
                      label="Prijavi se"
                      type="submit"
                      disabled={isLoginLoading}
                      isLoading={
                        isLoginLoading || isGetSubscriptionDetailsLoading
                      }
                      hasLoader
                    />
                  </Form>
                </Formik>
              </>
            ) : (
              <>
                <Formik
                  onSubmit={(values, actions) =>
                    registerMutate(
                      {
                        email: values.email,
                        password: values.password,
                        isSubscribedToNewsletter:
                          values.isSubscribedToNewsletter,
                        ...(isRecurringPlanType(subscriptionType)
                          ? { subscriptionType }
                          : {}),
                      },
                      {
                        onSuccess: () => {
                          navigate(ROUTES.registerSuccess, {
                            state: { email: values.email },
                          });
                          if (!isOneTimePlanType(subscriptionType)) {
                            setSubscriptionType({ subscriptionType: null });
                          }
                        },
                        onError: (response) => {
                          actions.setFieldError(
                            "email",
                            // @ts-ignore
                            response?.response?.data?.message ||
                              "Došlo je do greške. Pokušajte ponovo."
                          );
                        },
                      }
                    )
                  }
                  validationSchema={registerValidationSchema}
                  validateOnChange={false}
                  initialValues={{
                    email: "",
                    password: "",
                    confirmPassword: "",
                    isSubscribedToNewsletter: true,
                    policy: false,
                  }}
                >
                  <Form className="auth_form">
                    <Input
                      name="email"
                      label="Email"
                      placeholder="npr. ime@email.com"
                      type="email"
                      autoFocus
                      autoComplete="email"
                    />
                    <Input
                      name="password"
                      label="Lozinka"
                      type="password"
                      placeholder="Min. 8 karaktera"
                      autoComplete="new-password"
                    />
                    <Input
                      name="confirmPassword"
                      label="Ponovi lozinku"
                      type="password"
                      placeholder="Ponovite lozinku"
                      autoComplete="new-password"
                    />

                    <div className="auth_form-checks">
                      <Checkbox
                        name="policy"
                        label="Slažem se sa"
                        link={{
                          label: "uslovima korišćenja",
                          // @ts-ignore
                          onClick: () => navigate(ROUTES.legal),
                        }}
                      />
                      <Checkbox
                        name="isSubscribedToNewsletter"
                        label="Prijavi se na newsletter"
                      />
                    </div>

                    <Button
                      variant="primary"
                      label="Registruj se"
                      type="submit"
                      hasLoader
                      isLoading={isRegisterLoading}
                      disabled={isRegisterLoading}
                    />
                  </Form>
                </Formik>
              </>
            )}
          </div>
        </div>

        {isDesktop && (
          <Illustration className="hover-float auth_illustration" />
        )}
      </div>
    </div>
  );
};

export default Login;
